import React, { useEffect, useMemo, useState } from "react";
import { t } from "ttag";
import { BaseDialog, Checkbox, Row, Stack, Typography } from "@coolremote/shared";
import { useGetSiteModbusDeviceTemplates } from "../../../../hooks/useModbusDeviceTemplatesApi";
import { useGetSiteModbusFunctionalTemplates } from "../../../../hooks/useModbusFunctionalTemplatesApi";
import { IModbusDeviceTemplate, IModbusFunctionalTemplate } from "../../types";

interface IProps {
  open: boolean;
  siteId?: string;
  deviceTemplateName: string;
  /** Whether the device (its own fields, or any of its units) changed since load. Defaults to true — always offered — when omitted. */
  deviceChanged?: boolean;
  units: { name: string }[];
  /** Indices into `units` that changed since load. Omit to treat every unit as changed (shows them all). */
  changedUnitIndices?: Set<number>;
  saving?: boolean;
  onClose: () => void;
  onSave: (
    saveDevice: boolean,
    replaceDeviceId: string | null,
    unitIndices: number[],
    replaceUnitIds: (string | null)[],
  ) => void;
}

const SaveAsTemplateDialog: React.FC<IProps> = ({
  open,
  siteId,
  deviceTemplateName,
  deviceChanged = true,
  units,
  changedUnitIndices,
  saving,
  onClose,
  onSave,
}) => {
  const [saveDevice, setSaveDevice] = useState(true);
  const [selectedUnits, setSelectedUnits] = useState<boolean[]>([]);

  // Self-load the library (reloaded each open) to detect name conflicts here,
  // rather than having the parent pass in the full template lists.
  const { data: deviceTemplatesData, refetch: refetchDeviceTemplates } = useGetSiteModbusDeviceTemplates(siteId, open) as unknown as {
    data: IModbusDeviceTemplate[] | null;
    refetch: () => void;
  };
  const { data: functionalTemplatesData, refetch: refetchFunctionalTemplates } = useGetSiteModbusFunctionalTemplates(siteId, open) as unknown as {
    data: IModbusFunctionalTemplate[] | null;
    refetch: () => void;
  };

  const deviceConflictId = useMemo(
    () =>
      (deviceTemplatesData ?? []).find((tpl) => tpl.name === deviceTemplateName)?.id ?? null,
    [deviceTemplatesData, deviceTemplateName],
  );
  const unitConflictIds = useMemo(
    () => units.map((u) => (functionalTemplatesData ?? []).find((ft) => ft.name === u.name)?.id ?? null),
    [functionalTemplatesData, units],
  );

  useEffect(() => {
    if (open) {
      // Only pre-check items that actually changed — unchanged ones aren't
      // shown at all (see the render below), so they must default to
      // unselected or they'd be saved without the user ever seeing them.
      setSaveDevice(deviceChanged);
      setSelectedUnits(units.map((_, i) => !changedUnitIndices || changedUnitIndices.has(i)));
      refetchDeviceTemplates();
      refetchFunctionalTemplates();
    }
  }, [open, units.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleUnit = (i: number) =>
    setSelectedUnits((prev) => {
      const next = [...prev];
      next[i] = !next[i];
      return next;
    });

  const handleConfirm = () => {
    const unitIndices = units.map((_, i) => i).filter((i) => selectedUnits[i]);
    // Library names must stay unique — a name conflict always replaces the
    // existing entry rather than offering to save a second one under the same name.
    const replaceUnitIds = units.map((_, i) =>
      selectedUnits[i] && unitConflictIds?.[i] ? unitConflictIds[i]! : null,
    );
    const replaceDeviceId = saveDevice && deviceConflictId ? deviceConflictId : null;
    onSave(saveDevice, replaceDeviceId, unitIndices, replaceUnitIds);
  };

  const ConflictChoice = ({
    conflictId,
    active,
  }: {
    conflictId: string | null | undefined;
    active: boolean;
  }) => {
    if (!conflictId || !active) {
      return null;
    }
    return (
      <Typography variant="meta" color="muted" style={{ paddingTop: 4 }}>
        {t`An item with this name already exists in the Library — saving will replace it.`}
      </Typography>
    );
  };

  // Only offer units that actually changed since load; an omitted set treats
  // every unit as changed (matches the pre-filter default behavior).
  const visibleUnitIndices = units
    .map((_, i) => i)
    .filter((i) => !changedUnitIndices || changedUnitIndices.has(i));

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={t`Save to Library`}
      onConfirm={handleConfirm}
      confirmLabel={t`Save`}
      cancelLabel={t`Skip`}
      loading={saving}
      size="small"
      staticBackdrop
      footerLeftContent={
        <Typography variant="meta" color="muted">
          {t`You can save later when editing a device or a functional unit`}
        </Typography>
      }
    >
      <Stack gap={20}>
        <Typography variant="body">
          {t`Choose what to save into the Library. Saved items can be reused when configuring future devices.`}
        </Typography>

        {deviceChanged && (
          <Stack gap={12}>
            <Typography variant="sectionLabel">{t`Devices`}</Typography>
            <Typography variant="meta" color="muted">
              {t`Includes all units and their register mappings`}
            </Typography>
            <Stack gap={4}>
              <Row gap={10} style={{ cursor: "pointer" }} onClick={() => setSaveDevice((v) => !v)}>
                <Checkbox checked={saveDevice} onChange={() => setSaveDevice((v) => !v)} />
                <Typography variant="optionTitle">{deviceTemplateName}</Typography>
              </Row>
              <ConflictChoice conflictId={deviceConflictId} active={saveDevice} />
            </Stack>
          </Stack>
        )}

        {visibleUnitIndices.length > 0 && (
          <Stack gap={12}>
            <Typography variant="sectionLabel">{t`Functional Units`}</Typography>
            <Typography variant="meta" color="muted">
              {t`Select which units to save as reusable unit library.`}
            </Typography>
            <Stack gap={6}>
              {visibleUnitIndices.map((i) => (
                <Stack key={i} gap={4}>
                  <Row gap={10} style={{ cursor: "pointer" }} onClick={() => toggleUnit(i)}>
                    <Checkbox checked={!!selectedUnits[i]} onChange={() => toggleUnit(i)} />
                    <Typography variant="optionTitle">{units[i].name}</Typography>
                  </Row>
                  <ConflictChoice conflictId={unitConflictIds?.[i]} active={!!selectedUnits[i]} />
                </Stack>
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </BaseDialog>
  );
};

export default SaveAsTemplateDialog;
